/**
 *
 * @param {string} str input string
 * @param {int} maxMatchCount the max count you want to extract from the str
 * @param {bool} extractAll if returns all the extracted urls
 * @returns {Array} the extracted urls
 */
export function ExtractDirectUrl(str, maxMatchCount = 1, extractAll = true) {
  if (!str) {
    return [];
  }

  const arr = str.match(/\s*http(s)?:.*/g);

  return extractAll ?
    arr.map(i => i.replace(/[\s\t]/g, '')) :
    arr.map(i => i.replace(/[\s\t]/g, '')).slice(0, maxMatchCount);
}


/**
 *
 * @description extract the src attribute in htmlStr
 * support html tags are <img>, <script>, <iframe>
 * @param {string} htmlStr html string to be etracted , for example :
 * `<html>
    <head>
        <style href="123"/>
        <        script src='https://www.bing2.com'>123</script>
    </head>
    <body>

        <            img src="https://www.bing.com'>
        <        script src="https://www.bing1.com">1244444444444444443<             /script>
    </body>
  </html>`
 * @param {int} maxMatchCount the max count you want to extract from the htmlStr
 * @param {bool} extractAll if returns all the extracted urls
 * @returns {Array} the matched urls in the htmlStr
 */
export function extractUrlFromHtml(htmlStr, maxMatchCount = 1, extractAll = true) {
  if (!htmlStr) {
    return [];
  }

  const regex = new RegExp(/(<\s*?(img|script|iframe).*?src=["'].*?["'])/g);

  const ExtractedElements = htmlStr.match(regex)
    // set the tolerance that spaces exists after < , like <  img>
    .map(i => i.replace(/^<\s*/, '<'))
    // set the tolerance that spaces exists after < , like <  script>...<   /script>
    .map(i => i.replace(/<\s*\//, '</'));

  if (!ExtractedElements || !ExtractedElements.length) {
    return [];
  }

  const ExtractedUrls = ExtractedElements.map((i) => {
    const temp = i.match(/src=["'].*?["']/);

    if (temp) {
      return temp[0];
    }

    return null;
  })
    // filter out null
    .filter(i => !!i)
    .map((i) => {
      const temp = i.match(/["'].*?["']/);

      if (temp) {
        return temp[0].replace(/["']/g, '');
      }

      return null;
    })
    // filter out null
    .filter(i => !!i);

  if (!ExtractedUrls || !ExtractedUrls.length) {
    return [];
  }

  return extractAll ? ExtractedUrls : ExtractedUrls.slice(0, maxMatchCount);
}

/**
 * @description extracting the availiable url in the inputStr
 * @param {string} inputStr string contains url , currently suport :
 * 1. url begins with http(s)
 * 2. html code contains <img>, <script>, <iframe> with src attribute
 * 3. mixing point 1 and point2
 * @param {int} maxMatchCount the max count you want to extract from the htmlStr
 * @param {boolean} extractAll if returns all the extracted urls
 * @param {boolean} ifSkipDirectUrlExtraction if skip the extraction of direct url
 * @returns {Array} the matched urls in the inputStr
 *
 * Test inputStr need to be coverd:
 * 1. <img src="http://www.bing.com" alt="" width="0" height="0">
 * 2. <script charset="utf-8" src="https://outlook-1.cdn.office.net/owamail/2020072302/scripts/owa.12.js" crossorigin="anonymous"></script>
 * 3. <iframe src="https://webshell.suite.office.com/iframe/TokenFactoryIframe?origin=https%3A%2F%2Foutlook.office.com&amp;shsid=06230592-7001-4533-b6c7-2bc4e82bfe59&amp;apiver=oneshell&amp;cshver=20200722.2&amp;upn=xiao%40microsoft.com" id="o365shellwcssframe" name="o365shellwcssframe" sandbox="allow-scripts allow-same-origin allow-forms" style="display: none;"></iframe>
 * 4. http://www.bing.com
 * 5. `<html>
        <head>
            <style href="123"/>
            <        script src='https://www.bing2.com'>123</script>
        </head>
        <body>

            <            img src="https://www.bing.com'>
            <        script src="https://www.bing1.com">1244444444444444443<             /script>
        </body>
      </html>`
 *  6. `
        http://www.bing.com
        <html>
          <head>
              <style href="123"/>
              <        script src='https://www.bing2.com'>123</script>
          </head>
          <body>

              <            img src="https://www.bing.com'>
              <        script src="https://www.bing1.com">1244444444444444443<             /script>
          </body>
        </html>
        http://www.bing.com
        `
  * 7. (no \n to start a new line)
       <img src="http://tps.doubleverify.com/visit.jpg?ctx=13272290&cmp=23962578&sid=6058580&plc=271450684&adsrv=1&btreg=&btadsrv=&crt=&tagtype=&dvtagver=6.1.img&" alt="" width="0" height="0" /><img src="http://tps.doubleverify.com/visit.jpg?ctx=13272290&cmp=23962578&sid=6058580&plc=271450684&adsrv=1&btreg=&btadsrv=&crt=&tagtype=&dvtagver=6.1.img&" alt="" width="0" height="0" />
  * 8. (no \n to start a new line)
       <iframe src="http://google.com"></iframe><img src="http://tps.doubleverify.com/visit.jpg?ctx=13272290&cmp=23962578&sid=6058580&plc=271450684&adsrv=1&btreg=&btadsrv=&crt=&tagtype=&dvtagver=6.1.img&" alt="" width="0" height="0" />
 */
export function extractUrls(
  inputStr, maxMatchCount = 1,
  extractAll = false,
  ifSkipDirectUrlExtraction = true
) {
  if (!inputStr) {
    return [];
  }

  const seperator = new RegExp(/[\n]/g);

  // 1. split the inputStr by seperator
  const strSegements = inputStr.split(seperator);
  // 2. try to extract the url
  let result = [];

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < strSegements.length; i++) {
    const segement = strSegements[i];

    let urls = [];

    try {
      urls = extractUrlFromHtml(segement);
      result = result.concat(urls);
      // eslint-disable-next-line no-continue
      continue;
    } catch (e) {
      // extract for this line failed , do nothing, keep trying to extract other lines
    }

    if (!ifSkipDirectUrlExtraction) {
      try {
        urls = ExtractDirectUrl(segement);
        result = result.concat(urls);
        // eslint-disable-next-line no-continue
        continue;
      } catch (e) {
        // extract for this line failed , do nothing, keep trying to extract other lines
      }
    }
  }

  return extractAll ? result : result.slice(0, maxMatchCount);
}

export const extractIdsFromIASInput = (value) => {
  // e.g. input:
  // <script src="https://static.adsafeprotected.com/fw.js" data-ias-parameters='{"config":"/rjss/st/xxxxxx/yyyyyy/etc"}' ></script>
  // output:
  // ['xxxxx', 'yyyyyy']

  // e.g. input:
  // <AdVerifications>
  //   <Verification vendor="integralads.com">
  //     <JavaScriptResource apiFramework="omid" browserOptional="true">
  //      <![CDATA[https://static.adsafeprotected.com/fw.js]>
  //    </JavaScriptResource>
  //    <VerificationParameters>{"config":"/rjss/st/xxxxx/yyyyyyy/skeleton.js?",
  //      "sca":"hosted.js_for_fraud","xsca":"hosted.js_for_fraud_2"}
  //    </VerificationParameters>
  //   </Verification>
  // </AdVerifications>
  // output:
  // ['xxxxx', 'yyyyyy']

  // e.g. input:
  // <script src="https://static.adsafeprotected.com/fw.js" data-ias-parameters='{"config":"/fwjsvid/st/xxxxxx/yyyyyy/etc"}' ></script>
  // output:
  // ['xxxxx', 'yyyyyy']

  const regexpRjss = new RegExp(/rjss\/st\/(\w+)\/(\w+)\//);
  const regexpFwjsvid = new RegExp(/fwjsvid\/st\/(\w+)\/(\w+)\//);
  const result = regexpRjss.exec(value) || regexpFwjsvid.exec(value);
  if (!result) {
    return [];
  }
  return [result[1], result[2]];
};
